import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KiriCharacter } from "@/components/kiri/KiriCharacter";
import { OnboardingLivePreview } from "@/components/OnboardingLivePreview";
import { Button } from "@/components/ui/button";
import {
  Mic, MicOff, Video, VideoOff, ScreenShare, MoreHorizontal, Send, Monitor,
  CheckCircle2, Circle, Flag, MinusCircle, LogOut, Volume2, VolumeX, X, ArrowLeft,
} from "lucide-react";

const STEP_ICON = {
  completed: { Icon: CheckCircle2, cls: "text-emerald-500" },
  skipped: { Icon: MinusCircle, cls: "text-zinc-500" },
  not_applicable: { Icon: MinusCircle, cls: "text-zinc-600" },
  in_progress: { Icon: Circle, cls: "text-[#FF5F15] fill-[#FF5F15]/30" },
  needs_followup: { Icon: Flag, cls: "text-amber-500" },
  not_started: { Icon: Circle, cls: "text-zinc-600" },
};

function elapsedLabel(startedAt) {
  if (!startedAt) return "0:00";
  const secs = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
  const m = Math.floor(secs / 60), s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function OnboardingSession() {
  const { id } = useParams();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [kiriState, setKiriState] = useState("idle");
  const [tab, setTab] = useState("chat");
  const [elapsed, setElapsed] = useState("0:00");
  const [recording, setRecording] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [voiceMuted, setVoiceMuted] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [muted, setMuted] = useState(false);
  const [screenView, setScreenView] = useState(null);
  const [viewMode, setViewMode] = useState("kiri");

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioElRef = useRef(null);
  const videoElRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const maxDurationTimerRef = useRef(null);
  const hasSpokenRef = useRef(false);
  const rafRef = useRef(null);

  useEffect(() => {
    api.get(`/guided-onboarding/sessions/${id}`)
      .then(({ data }) => { setSession(data); setMessages(data.messages || []); })
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't load this session"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!session?.started_at) return;
    const t = setInterval(() => setElapsed(elapsedLabel(session.started_at)), 1000);
    return () => clearInterval(t);
  }, [session?.started_at]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  useEffect(() => () => { streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);

  const stopSpeaking = useCallback(() => {
    audioElRef.current?.pause();
    setSpeaking(false);
  }, []);

  const playReply = useCallback(async (text, { retry = true } = {}) => {
    if (voiceMuted || !text) return;
    try {
      const { data } = await api.get(`/guided-onboarding/sessions/${id}/tts`, { params: { text }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const audioEl = new Audio(url);
      audioElRef.current = audioEl;
      setSpeaking(true);
      audioEl.onended = () => setSpeaking(false);
      await audioEl.play();
    } catch (e) {
      setSpeaking(false);
      if (retry) { playReply(text, { retry: false }); return; }
      toast.error("Couldn't play Kiri's voice for that reply — showing text instead.");
    }
  }, [voiceMuted, id]);

  const openScreen = useCallback((data, isTour) => {
    if (!data) return;
    setScreenView(isTour ? { route: data.route, tour: data } : { route: data.path, tour: null });
    setViewMode("screen");
  }, []);

  const applyTurn = useCallback((turn) => {
    setSession((s) => ({ ...s, ...turn.session }));
    setKiriState("idle");
    setMessages((m) => [...m, { role: "assistant", content: turn.reply, result: turn.result, pending_action: turn.pending_action, created_at: new Date().toISOString() }]);
    if (turn.result?.type === "start_tour" && turn.result.data) {
      openScreen(turn.result.data, true);
    } else if (turn.result?.type === "navigate" && turn.result.data) {
      openScreen(turn.result.data, false);
    }
    playReply(turn.reply);
  }, [openScreen, playReply]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", content: msg, created_at: new Date().toISOString() }]);
    setInput("");
    setSending(true);
    setKiriState("thinking");
    try {
      const { data } = await api.post(`/guided-onboarding/sessions/${id}/chat`, { message: msg });
      applyTurn(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Kiri couldn't answer just now");
      setKiriState("idle");
    } finally {
      setSending(false);
    }
  };

  const toggleMic = async () => {
    if (muted) return;
    if (recording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      hasSpokenRef.current = false;
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);

      // Silence detection — auto-stop ~1.1s after speech trails off, so the
      // mic never keeps "listening" indefinitely to background noise.
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioCtx();
      audioCtxRef.current = audioCtx;
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      audioCtx.createMediaStreamSource(stream).connect(analyser);
      const freqData = new Uint8Array(analyser.frequencyBinCount);
      const SPEAK_THRESHOLD = 14, SILENCE_MS = 1100;
      const checkVolume = () => {
        if (mr.state !== "recording") return;
        analyser.getByteFrequencyData(freqData);
        const avg = freqData.reduce((a, b) => a + b, 0) / freqData.length;
        if (avg > SPEAK_THRESHOLD) {
          hasSpokenRef.current = true;
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = setTimeout(() => { if (mr.state === "recording") mr.stop(); }, SILENCE_MS);
        } else if (hasSpokenRef.current && !silenceTimerRef.current) {
          silenceTimerRef.current = setTimeout(() => { if (mr.state === "recording") mr.stop(); }, SILENCE_MS);
        }
        rafRef.current = requestAnimationFrame(checkVolume);
      };

      mr.onstop = async () => {
        cancelAnimationFrame(rafRef.current);
        clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null;
        clearTimeout(maxDurationTimerRef.current);
        audioCtx.close().catch(() => {});
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 800) { setKiriState("idle"); return; }
        setKiriState("thinking");
        setSending(true);
        const form = new FormData();
        form.append("file", blob, "clip.webm");
        try {
          const { data } = await api.post(`/guided-onboarding/sessions/${id}/voice`, form, { headers: { "Content-Type": "multipart/form-data" } });
          setMessages((m) => [...m, { role: "user", content: data.transcript, created_at: new Date().toISOString() }]);
          applyTurn(data);
        } catch (e) {
          toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't hear that clearly");
          setKiriState("idle");
        } finally {
          setSending(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      setKiriState("listening");
      rafRef.current = requestAnimationFrame(checkVolume);
      maxDurationTimerRef.current = setTimeout(() => { if (mr.state === "recording") mr.stop(); }, 45000);
    } catch {
      toast.error("Microphone access is blocked — allow it in your browser to use voice.");
    }
  };

  const toggleCamera = async () => {
    if (cameraOn) {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setCameraOn(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      streamRef.current = stream;
      if (videoElRef.current) videoElRef.current.srcObject = stream;
      setCameraOn(true);
    } catch {
      toast.error("Camera access is blocked — allow it in your browser to use video.");
    }
  };

  const executeAction = async (msgIdx, action) => {
    try {
      const { data } = await api.post(`/guided-onboarding/sessions/${id}/actions/execute`, { action_id: action.id, params: action.params });
      setSession((s) => ({ ...s, ...data.session }));
      setMessages((m) => m.map((mm, i) => (i === msgIdx ? { ...mm, pending_action: null } : mm)).concat({
        role: "assistant", content: data.message, result: data.result, created_at: new Date().toISOString(),
      }));
      toast.success("Done — added to your setup.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't create that");
    }
  };

  const endSession = async (status) => {
    try {
      await api.patch(`/guided-onboarding/sessions/${id}`, { status });
      nav("/app");
    } catch { nav("/app"); }
  };

  const onFinishScreen = async (status) => {
    const tour = screenView?.tour;
    if (tour) {
      try {
        await api.patch(`/guided-onboarding/sessions/${id}`, { step_id: tour.step_id, step_status: status });
        const { data } = await api.get(`/guided-onboarding/sessions/${id}`);
        setSession(data);
      } catch { /* non-critical */ }
      if (status === "completed") toast.success("Nice — that's covered. Let's carry on with your setup.");
      setScreenView(null);
    }
    setViewMode("kiri");
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-500 text-xs uppercase tracking-widest font-mono">Loading…</div>;
  if (!session) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-500 text-sm">Session not found.</div>;

  const currentStep = session.plan.find((s) => s.id === session.current_step_id);

  return (
    <div className="h-screen bg-[#0B0F14] text-zinc-100 flex flex-col overflow-hidden" data-testid="onboarding-session-page">
      <header className="flex items-center justify-between px-5 py-3 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/app")} className="text-zinc-500 hover:text-white" data-testid="onboarding-back-btn"><ArrowLeft className="w-4 h-4" /></button>
          <span className="font-black text-sm tracking-wide">FIELDLINK</span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">AI Onboarding Session</span>
          <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-400" data-testid="onboarding-live-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live · {elapsed}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-mono text-zinc-400" data-testid="onboarding-progress-chip">{session.progress_pct}% complete</span>
          <Button size="sm" variant="outline" className="rounded-md h-8 text-[11px] font-bold uppercase" onClick={() => endSession("active")} data-testid="onboarding-finish-later-btn">Finish for now</Button>
          <Button size="sm" className="rounded-md h-8 text-[11px] font-bold uppercase bg-white/10 hover:bg-white/20" onClick={() => endSession("completed")} data-testid="onboarding-end-btn">
            <LogOut className="w-3.5 h-3.5 mr-1" /> End
          </Button>
        </div>
      </header>

      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_360px] overflow-hidden">
        {/* LEFT — Kiri "meeting" panel / embedded live screen */}
        <div className="relative flex flex-col items-center justify-center p-4 md:p-8 border-r border-white/10 min-h-0 overflow-y-auto">
          {viewMode === "screen" && screenView ? (
            <div className="absolute inset-3 md:inset-6 flex flex-col" data-testid="onboarding-screen-mode">
              <OnboardingLivePreview view={screenView} onFinish={onFinishScreen} />
              <button onClick={() => setViewMode("kiri")} className="absolute top-3 right-3 z-[65] bg-[#12161C]/90 hover:bg-[#1A1F27] border border-white/10 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-300 flex items-center gap-1.5" data-testid="onboarding-back-to-kiri-btn">
                <ArrowLeft className="w-3 h-3" /> Back to Kiri
              </button>
            </div>
          ) : (
            <>
              {cameraOn && (
                <div className="absolute top-4 right-4 w-28 h-20 rounded-lg overflow-hidden border border-white/20 bg-black" data-testid="onboarding-self-camera">
                  <video ref={videoElRef} autoPlay muted playsInline className="w-full h-full object-cover" />
                  <span className="absolute bottom-0.5 left-1 text-[9px] font-mono text-white/80">You</span>
                </div>
              )}
              <KiriCharacter useHero size="xl" state={kiriState === "thinking" ? "thinking" : recording ? "listening" : "idle"} />
              <p className="text-lg font-black text-white mt-4">KIRI</p>
              <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Your Fieldlink Onboarding Guide</p>
              {recording && (
                <div className="flex items-center gap-2 mt-3" data-testid="onboarding-listening-indicator">
                  <span className="w-2 h-2 rounded-full bg-[#FF5F15] animate-pulse" />
                  <span className="text-[11px] font-mono uppercase tracking-widest text-[#FF5F15]">Listening… tap mic to stop</span>
                </div>
              )}
              {speaking && (
                <div className="flex items-center gap-2 mt-3" data-testid="onboarding-speaking-indicator">
                  <div className="flex items-end gap-0.5 h-4">
                    {[0, 1, 2, 3].map((i) => <span key={i} className="w-0.5 bg-[#FF5F15] rounded-full animate-pulse" style={{ height: `${6 + (i % 2) * 8}px`, animationDelay: `${i * 100}ms` }} />)}
                  </div>
                  <button onClick={stopSpeaking} className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 hover:text-white flex items-center gap-1" data-testid="onboarding-stop-speaking-btn">
                    <Volume2 className="w-3 h-3" /> Stop
                  </button>
                </div>
              )}
              {(() => {
                const last = messages[messages.length - 1];
                if (!last || last.role !== "assistant") return null;
                return (
                  <div className="mt-6 max-w-md text-center" data-testid="onboarding-latest-reply">
                    <p className="text-zinc-300 text-sm leading-relaxed">"{last.content}"</p>
                    <MessageActions msg={last} idx={messages.length - 1} onExecute={executeAction} onSend={send} onOpenScreen={openScreen} />
                  </div>
                );
              })()}

              {screenView && (
                <button onClick={() => setViewMode("screen")} className="mt-4 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#FF5F15] hover:text-[#ff7a3d]" data-testid="onboarding-view-screen-btn">
                  <Monitor className="w-3.5 h-3.5" /> View Screen
                </button>
              )}

              <div className="flex items-center gap-2 mt-8">
                <button onClick={() => setMuted((v) => !v)} className={`w-10 h-10 rounded-full flex items-center justify-center border ${muted ? "bg-red-500/20 border-red-500/40 text-red-400" : "bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10"}`} data-testid="onboarding-mute-btn" aria-label="Mute">
                  {muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
                <button onClick={toggleMic} disabled={muted} className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${recording ? "bg-[#FF5F15] border-[#FF5F15] text-white" : "bg-white/5 border-white/20 text-zinc-200 hover:bg-white/10"} disabled:opacity-40`} data-testid="onboarding-record-btn" aria-label="Ask with voice">
                  <Mic className="w-5 h-5" />
                </button>
                <button onClick={toggleCamera} className={`w-10 h-10 rounded-full flex items-center justify-center border ${cameraOn ? "bg-[#FF5F15]/20 border-[#FF5F15]/40 text-[#FF5F15]" : "bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10"}`} data-testid="onboarding-video-btn" aria-label="Toggle camera">
                  {cameraOn ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
                </button>
                <button onClick={() => setVoiceMuted((v) => !v)} className={`w-10 h-10 rounded-full flex items-center justify-center border ${voiceMuted ? "bg-white/5 border-white/10 text-zinc-500" : "bg-[#FF5F15]/20 border-[#FF5F15]/40 text-[#FF5F15]"}`} data-testid="onboarding-voice-toggle-btn" aria-label="Toggle Kiri's voice">
                  {voiceMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                <button onClick={() => toast.info("Screen sharing isn't needed — Kiri already knows what page you're viewing inside Fieldlink.")} className="w-10 h-10 rounded-full flex items-center justify-center border bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10" data-testid="onboarding-share-btn" aria-label="Share screen">
                  <ScreenShare className="w-4 h-4" />
                </button>
                <button onClick={() => { setTab("chat"); inputRef.current?.focus(); }} className="ml-1 h-10 px-4 rounded-full bg-white/10 hover:bg-white/20 text-xs font-bold uppercase tracking-wider flex items-center gap-2" data-testid="onboarding-ask-question-btn">
                  <MoreHorizontal className="w-4 h-4" /> Ask a Question
                </button>
              </div>
            </>
          )}
        </div>

        {/* RIGHT — tabs */}
        <div className="flex flex-col bg-[#0E1319] min-h-0">
          <div className="flex border-b border-white/10 shrink-0">
            {["chat", "agenda", "notes", "resources"].map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`onboarding-tab-${t}`}
                className={`flex-1 py-3 text-[11px] font-bold uppercase tracking-wider ${tab === t ? "text-[#FF5F15] border-b-2 border-[#FF5F15]" : "text-zinc-500 hover:text-zinc-300"}`}>
                {t}
              </button>
            ))}
          </div>

          {tab === "chat" && (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="onboarding-chat-log">
                {messages.map((m, i) => <ChatBubble key={i} msg={m} idx={i} onExecute={executeAction} onSend={send} onOpenScreen={openScreen} />)}
                {sending && <div className="flex items-center gap-2 text-zinc-500 text-xs"><KiriCharacter size="xs" state="thinking" /> Kiri is thinking…</div>}
              </div>
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 p-3 border-t border-white/10 shrink-0">
                <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask Kiri anything…"
                  className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#FF5F15]/50" data-testid="onboarding-chat-input" />
                <button type="submit" disabled={sending || !input.trim()} className="w-10 h-10 rounded-full bg-[#FF5F15] hover:bg-[#E65100] disabled:opacity-40 flex items-center justify-center text-white shrink-0" data-testid="onboarding-chat-send-btn">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}

          {tab === "agenda" && <AgendaTab session={session} onJump={(label) => send(`Let's do ${label} next.`)} />}
          {tab === "notes" && <NotesTab discovery={session.discovery} />}
          {tab === "resources" && <ResourcesTab />}
        </div>
      </div>
    </div>
  );
}

function MessageActions({ msg, idx, onExecute, onSend, onOpenScreen }) {
  const r = msg.result;
  if (msg.pending_action) return <PendingActionCard action={msg.pending_action} idx={idx} onExecute={onExecute} />;
  if (r?.type === "navigate" && r.data) {
    return (
      <Button size="sm" className="mt-3 rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" onClick={() => onOpenScreen(r.data, false)} data-testid={`onboarding-show-me-${idx}`}>
        Show Me <Monitor className="w-3.5 h-3.5 ml-1" />
      </Button>
    );
  }
  if (r?.type === "start_tour" && r.data) {
    return (
      <Button size="sm" className="mt-3 rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" onClick={() => onOpenScreen(r.data, true)} data-testid={`onboarding-reopen-tour-${idx}`}>
        Show Me <Monitor className="w-3.5 h-3.5 ml-1" />
      </Button>
    );
  }
  if (r?.type === "low_confidence") {
    return (
      <div className="flex items-center gap-2 justify-center mt-3">
        <Button size="sm" variant="outline" className="rounded-md text-[11px] font-bold uppercase tracking-wider" onClick={() => onSend(`Please ask Fieldlink Support: ${r.data.question}`)} data-testid={`onboarding-ask-support-${idx}`}>Ask Support</Button>
        <Button size="sm" className="rounded-md bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider" onClick={() => onSend("Let's continue onboarding.")} data-testid={`onboarding-continue-${idx}`}>Continue Onboarding</Button>
      </div>
    );
  }
  if (r?.type === "escalated") {
    return <p className="mt-3 text-[11px] text-emerald-400 font-mono uppercase tracking-widest">Support ticket raised — we'll follow up.</p>;
  }
  return null;
}

function ChatBubble({ msg, idx, onExecute, onSend, onOpenScreen }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`onboarding-msg-${idx}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${isUser ? "bg-[#FF5F15] text-white" : "bg-white/5 text-zinc-200 border border-white/10"}`}>
        {msg.content}
        <MessageActions msg={msg} idx={idx} onExecute={onExecute} onSend={onSend} onOpenScreen={onOpenScreen} />
      </div>
    </div>
  );
}

function PendingActionCard({ action, idx, onExecute }) {
  const [positions, setPositions] = useState(action.params?.positions || []);
  return (
    <div className="mt-3 bg-[#0B0F14] border border-[#FF5F15]/30 rounded-lg p-3 text-left" data-testid={`onboarding-pending-action-${idx}`}>
      <p className="text-[10px] font-mono uppercase tracking-widest text-[#FF5F15] font-bold">{action.label} — Draft</p>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {positions.map((p, i) => (
          <span key={p} className="flex items-center gap-1 bg-white/10 rounded-full px-2.5 py-1 text-xs">
            {p}
            <button onClick={() => setPositions((ps) => ps.filter((_, ix) => ix !== i))} data-testid={`onboarding-remove-position-${i}`}><X className="w-3 h-3 text-zinc-400 hover:text-white" /></button>
          </span>
        ))}
      </div>
      <Button size="sm" disabled={!positions.length} className="mt-3 rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" onClick={() => onExecute(idx, { ...action, params: { ...action.params, positions } })} data-testid={`onboarding-create-action-${idx}`}>
        Create
      </Button>
    </div>
  );
}

function AgendaTab({ session, onJump }) {
  return (
    <div className="flex-1 overflow-y-auto p-4" data-testid="onboarding-agenda-tab">
      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Your Setup Plan</p>
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#FF5F15] rounded-full" style={{ width: `${session.progress_pct}%` }} /></div>
        <span className="text-sm font-bold text-white">{session.progress_pct}%</span>
      </div>
      <div className="space-y-1">
        {session.plan.filter((s) => s.status !== "not_applicable").map((s) => {
          const { Icon, cls } = STEP_ICON[s.status] || STEP_ICON.not_started;
          const active = s.id === session.current_step_id;
          return (
            <button key={s.id} onClick={() => onJump(s.label)} disabled={s.status === "completed"}
              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left text-sm ${active ? "bg-white/10" : "hover:bg-white/5"} disabled:cursor-default`}
              data-testid={`onboarding-plan-step-${s.id}`}>
              <Icon className={`w-4 h-4 shrink-0 ${cls}`} />
              <span className={s.status === "completed" || s.status === "skipped" ? "text-zinc-500 line-through" : "text-zinc-200"}>{s.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NotesTab({ discovery = {} }) {
  const rows = [
    ["Business", discovery.business_type],
    ["Team", discovery.team_size],
    ["Approval", discovery.approval_workflow === true ? "Foremen approve timesheets" : discovery.approval_workflow === false ? "No formal approval step yet" : null],
    ["Plant", discovery.uses_plant === true ? "Fleet setup required" : discovery.uses_plant === false ? "No plant to set up" : null],
    ["Commercial", discovery.uses_quotes === true ? "Uses quoting + variations" : discovery.uses_quotes === false ? "Not quoting through Fieldlink" : null],
    ["Integration", discovery.uses_xero === true ? "Uses Xero" : discovery.uses_xero === false ? "No accounting integration" : null],
  ].filter(([, v]) => v);
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="onboarding-notes-tab">
      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Session Notes</p>
      {rows.length === 0 && <p className="text-zinc-500 text-sm">Nothing captured yet — tell Kiri about your business as you chat.</p>}
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between border-b border-white/5 pb-2">
          <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">{k}</span>
          <span className="text-sm text-zinc-200 text-right max-w-[60%]">{String(v)}</span>
        </div>
      ))}
    </div>
  );
}

function ResourcesTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/guided-onboarding/resources").then(({ data }) => setItems(data.items || [])).catch(() => {}); }, []);
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="onboarding-resources-tab">
      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Resources</p>
      {items.map((it) => (
        <Link key={it.to} to={it.to} className="block bg-white/5 hover:bg-white/10 rounded-lg p-3 border border-white/10" data-testid={`onboarding-resource-${it.to}`}>
          <p className="text-sm font-bold text-white">{it.title}</p>
          <p className="text-xs text-zinc-500 mt-0.5">{it.desc}</p>
        </Link>
      ))}
    </div>
  );
}
